import React, { useState } from 'react'
import { Search, X, Filter as FilterIcon, RotateCcw, Loader2 } from 'lucide-react'

export interface FilterOption {
  label: string
  value: string | number | boolean
}

export interface FilterFieldConfig {
  key: string
  label: string
  type: 'select' | 'text' | 'date'
  options?: FilterOption[]
  placeholder?: string
}

export interface AdminFiltersProps {
  fields?: FilterFieldConfig[]
  search?: string
  searchPlaceholder?: string
  onSearchChange?: (value: string) => void
  filters?: Record<string, any>
  onFilterChange?: (key: string, value: any) => void
  onClearFilters?: () => void
  onRemoveFilter?: (key: string) => void
  hasActiveFilters?: boolean
  isLoading?: boolean
  className?: string
}

export default function AdminFilters({
  fields = [],
  search = '',
  searchPlaceholder = 'Search...',
  onSearchChange,
  filters = {},
  onFilterChange,
  onClearFilters,
  onRemoveFilter,
  hasActiveFilters = false,
  isLoading = false,
  className = '',
}: AdminFiltersProps) {
  const [mobileOpen, setMobileOpen] = useState(false)

  // Format chip label for active filter display
  const getChipLabel = (key: string, val: any) => {
    if (key === 'search') return `Search: "${val}"`
    const field = fields.find((f) => f.key === key)
    if (!field) return `${key}: ${val}`
    if (field.type === 'select' && field.options) {
      const opt = field.options.find((o) => String(o.value) === String(val))
      return `${field.label}: ${opt ? opt.label : val}`
    }
    return `${field.label}: ${val}`
  }

  // Active filter items for chips
  const activeChips: Array<{ key: string; label: string }> = []
  if (search && search.trim()) {
    activeChips.push({ key: 'search', label: `Search: "${search.trim()}"` })
  }
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== 'all') {
      activeChips.push({ key: k, label: getChipLabel(k, v) })
    }
  })

  return (
    <div className={`bg-white rounded-xl shadow-sm border border border-gray-200 p-4 mb-6 space-y-4 ${className}`}>
      {/* Top Row: Search Input & Action Buttons */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Search Bar */}
        {onSearchChange && (
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-3 text-gray-400" size={18} />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-9 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] focus:border-transparent transition-all"
            />
            {search && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}

        {/* Mobile Filter Toggle & Desktop Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Mobile Filter Toggle Button */}
          {fields.length > 0 && (
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="md:hidden flex items-center gap-2 px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <FilterIcon size={16} className="text-[#6D3A0D]" />
              <span>Filters</span>
              {activeChips.length > 0 && (
                <span className="bg-[#6D3A0D] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {activeChips.length}
                </span>
              )}
            </button>
          )}

          {/* Reset/Clear All Filters Button */}
          {hasActiveFilters && onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold text-[#6D3A0D] bg-[#F6EEE7] hover:bg-[#ebdcd0] rounded-lg transition-colors border border-[#e6d0c0]"
            >
              <RotateCcw size={14} />
              <span>Clear Filters</span>
            </button>
          )}

          {isLoading && <Loader2 size={18} className="animate-spin text-[#6D3A0D] ml-1" />}
        </div>
      </div>

      {/* Desktop Filter Dropdowns */}
      {fields.length > 0 && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 ${mobileOpen ? 'block' : 'hidden md:grid'}`}>
          {fields.map((field) => {
            const currentValue = filters[field.key] ?? ''

            if (field.type === 'select') {
              const defaultLabel = field.placeholder
                ? field.placeholder
                : field.label.toLowerCase().endsWith('status')
                ? `All ${field.label}es`
                : field.label.toLowerCase().endsWith('y') && !['a', 'e', 'i', 'o', 'u'].includes(field.label.toLowerCase().slice(-2, -1))
                ? `All ${field.label.slice(0, -1)}ies`
                : field.label.toLowerCase().endsWith('s')
                ? `All ${field.label}`
                : `All ${field.label}s`

              return (
                <div key={field.key} className="flex flex-col">
                  <label htmlFor={`filter-${field.key}`} className="text-xs font-medium text-gray-600 mb-1">
                    {field.label}
                  </label>
                  <select
                    id={`filter-${field.key}`}
                    value={currentValue}
                    onChange={(e) => onFilterChange?.(field.key, e.target.value)}
                    disabled={isLoading}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] focus:border-transparent transition-all disabled:opacity-50"
                  >
                    <option value="">{defaultLabel}</option>
                    {field.options?.map((opt) => (
                      <option key={String(opt.value)} value={String(opt.value)}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )
            }

            if (field.type === 'date') {
              return (
                <div key={field.key} className="flex flex-col">
                  <label htmlFor={`filter-${field.key}`} className="text-xs font-medium text-gray-600 mb-1">
                    {field.label}
                  </label>
                  <input
                    type="date"
                    id={`filter-${field.key}`}
                    value={currentValue}
                    onChange={(e) => onFilterChange?.(field.key, e.target.value)}
                    disabled={isLoading}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] transition-all disabled:opacity-50"
                  />
                </div>
              )
            }

            return (
              <div key={field.key} className="flex flex-col">
                <label htmlFor={`filter-${field.key}`} className="text-xs font-medium text-gray-600 mb-1">
                  {field.label}
                </label>
                <input
                  type="text"
                  id={`filter-${field.key}`}
                  value={currentValue}
                  placeholder={field.placeholder || `Filter ${field.label}`}
                  onChange={(e) => onFilterChange?.(field.key, e.target.value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] transition-all disabled:opacity-50"
                />
              </div>
            )
          })}
        </div>
      )}

      {/* Active Filter Chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
          <span className="text-xs font-medium text-gray-500">Active Filters:</span>
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#F6EEE7] text-[#6D3A0D] border border-[#e6d0c0] rounded-full text-xs font-medium animate-fade-in"
            >
              <span>{chip.label}</span>
              {onRemoveFilter && (
                <button
                  type="button"
                  onClick={() => onRemoveFilter(chip.key)}
                  className="hover:text-red-600 transition-colors p-0.5"
                  title={`Remove ${chip.key}`}
                >
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
